import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import axios from 'axios';
import { AWS_LINK, DEV_LINK } from "../Static/static";

// 1. Define the Validation Schema with Yup
const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm your password'),
});

const ForgotPasswordRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;

  // Extract token from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jwtToken = params.get('token');
    console.log(jwtToken)
    if (!jwtToken) {
      setServerError("Invalid or missing reset token.");
    } else {
      setToken(jwtToken);
    }
  }, [location]);

  // 2. Initialize Formik
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setServerError('');
      try {
        const response = await axios.post(
            `${LINK}/forgot_password_reset`,
            // 2nd arg — request body (axios serializes this to JSON automatically)
            { 
              password: values.password, 
              password_confirm: values.confirmPassword 
            },
            // 3rd arg — config (headers, params, etc.)
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                // Content-Type: application/json is set automatically by axios, no need to add it
              },
            }
          );

        if (response.data.status === "OK") {
          setSuccess(true);
          setTimeout(() => navigate('/login'), 3000);
        } else {
          const data = await response.json();
          setServerError(data.message || "Link may have expired.");
        }
      } catch (err) {
        setServerError("Connection error. Please try again later.");
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <LockReset color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography component="h1" variant="h5">Reset Password</Typography>
          </Box>

          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Success! Redirecting...</Alert>}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="password"
              name="password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            />

            <Button
              color="primary"
              variant="contained"
              fullWidth
              type="submit"
              disabled={formik.isSubmitting || success || !!serverError && serverError.includes("token")}
              sx={{ mt: 3, py: 1.2 }}
            >
              {formik.isSubmitting ? <CircularProgress size={24} /> : 'Update Password'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordRequest;